/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useMemo, useRef } from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, Popover, PopoverBody, UncontrolledDropdown } from 'reactstrap';
import AIEditPencil from '@g17eco/images/ai-edit-pencil.svg';
import AIShortenCut from '@g17eco/images/ai-shorten-cut.svg';
import { RefineAction } from '@api/ai';
import { AIConfig } from '@g17eco/types/ai';
import { AIInstructionsForm } from '@features/ai/ai-instructions-form/AIInstructionsForm';
import { useAIInstructionsForm } from '@features/ai/ai-instructions-form/useAIInstructionsForm';
import { useToggle } from '@hooks/useToggle';
import { useClickOutside } from '@hooks/useClickOutside';

export interface HandleRefineData extends AIConfig {
  action: RefineAction;
}

interface Props {
  handleRefine: (data: HandleRefineData) => void;
  textContent: string | undefined;
  utrCode: string;
  disabled?: boolean;
}
export const AIRefineButton = ({ handleRefine, textContent = '', utrCode, disabled = false }: Props) => {
  const hasContent = textContent.trim().length > 0;
  const [modalOpen, toggleModal] = useToggle();

  // Store the AI instructions in local storage for the given Universal Tracker (utrCode),
  // therefore it will be reused for all AI Refine functionality like notes/answer.
  const storageKey = `ai-instructions-${utrCode}`;
  const { form, onChangeForm, saveToStorage } = useAIInstructionsForm({ storageKey });
  const aiBtnRef = useRef<HTMLButtonElement>(null);

  const options = useMemo(
    () => [
      {
        label: (
          <span>
            <img src={AIEditPencil} alt={hasContent ? 'Rewrite' : 'Write'} className='mr-2' />{' '}
            {hasContent ? 'Rewrite' : 'Write'}
          </span>
        ),
        value: RefineAction.Rewrite,
      },
      {
        label: (
          <span>
            <img src={AIShortenCut} alt='Shorten' className='mr-2' /> Shorten
          </span>
        ),
        value: RefineAction.Shorten,
        disabled: !hasContent,
      },
      {
        label: (
          <span>
            <i className='fa-light fa-input-text mr-2' /> AI Instructions
          </span>
        ),
        value: RefineAction.AddContext,
      },
    ],
    [hasContent],
  );

  const handleCloseModal = () => {
    saveToStorage();
    toggleModal();
  };

  const aiInstructionsRef = useRef<HTMLDivElement>(null);
  useClickOutside(aiInstructionsRef, handleCloseModal);

  const handleSelect = (action: RefineAction) => {
    if (action === RefineAction.AddContext) {
      toggleModal();
      return;
    }

    handleRefine({ action, additionalContext: form.additionalContext, aiModel: form.aiModel });
  };

  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle
          color='transparent'
          className='fs-6 px-1'
          disabled={disabled}
          data-testid='ai-refine-button'
          innerRef={aiBtnRef}
        >
          <i className='fa-light fa-stars'></i>
        </DropdownToggle>
        <DropdownMenu>
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              onClick={() => !option.disabled && handleSelect(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
      <Popover
        isOpen={modalOpen}
        placement='left-start'
        container='body'
        hideArrow
        target={aiBtnRef}
        className='ai-instructions-popover'
      >
        <PopoverBody>
          <div ref={aiInstructionsRef}>
            <AIInstructionsForm
              form={form}
              onChangeForm={onChangeForm}
              placeholder='Pass additional information or instructions to the AI to change the tone or improve its response by giving it extra data. Select Rewrite to send this info over.'
            />
          </div>
        </PopoverBody>
      </Popover>
    </>
  );
};
